import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type { Nps, DealerNpsFormValues, Dealer } from '../dealer.types';
import { BackendRoutes } from '../dealer.const';
import { useGtmTracking } from './use-gtm-tracking';
import { getDealerHttpService } from '../../../domain/http/http.factory';
import { useDSToast } from '@stihl-design-system/components';
const useDealerNps = ({ dealer }: { dealer?: Dealer }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['shared']);
  const [isLoading, setIsLoading] = useState(false);
  const { trackPromotion } = useGtmTracking();
  const { queueDSToastMessage } = useDSToast();

  /*********************************************
   *                 DATA-FETCHING
   *********************************************/

  const npsQuery = useQuery(['NPS'], () => getDealerHttpService().get(BackendRoutes.DEALER_NPS), {
    enabled: !!dealer?.id,
    select: (data: Nps[]) => data.find((nps) => nps.dealerId === dealer?.id),
  });

  const npsMutation = useMutation(
    ['NPS'],
    (
      values: Omit<DealerNpsFormValues, 'radioValue' | 'sliderValue'> & {
        dealerId: string;
      }
    ) =>
      getDealerHttpService().post(BackendRoutes.DEALER_NPS__DEALER_ID.replace(':dealerId', values.dealerId), {
        value: parseInt(String(values.value), 10),
        description: values.description,
      })
  );

  useEffect(() => {
    if (npsMutation.isSuccess) {
      setIsLoading(false);
      queueDSToastMessage(t('shared_dealers_map_detail_nps_success_message'), {
        variant: 'success',
      });
      queryClient.invalidateQueries(['NPS']);
    } else if (npsMutation.isError) {
      setIsLoading(false);
      queueDSToastMessage(t('shared_dealers_map_detail_nps_error_message'), {
        variant: 'warning',
      });
    }
  }, [npsMutation.isSuccess, npsMutation.isError]);

  /*********************************************
   *                 FORM
   *********************************************/

  const _validationSchema = useMemo(() => {
    return yup.object().shape({
      value: yup.string().required(t('shared_dealers_form_validationMessage_required_nps_score')).nullable(),
      description: yup.string().max(1000, t('shared_dealers_form_validationMessage_maxLength_nps_message')),
    });
  }, [t]);

  const methods = useForm<DealerNpsFormValues>({
    resolver: yupResolver(_validationSchema),
    defaultValues: {
      value: String(npsQuery.data?.value),
      description: '',
    },
  });

  const onSubmit = () => {
    if (!dealer?.id) {
      return;
    }
    setIsLoading(true);
    dealer && trackPromotion(dealer, 'rate-dealer');
    npsMutation?.mutate({ dealerId: dealer.id, ...methods.getValues() });
    methods.resetField('description');
    methods.resetField('value');
  };

  return {
    npsQuery,
    npsMutation,

    formProps: {
      methods,
      onSubmit: methods.handleSubmit(onSubmit),
      isLoading,
      setValue: methods.setValue,
    },
  };
};

export { useDealerNps };
